import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AiSparkIcon from '../common/AiSparkIcon';
import { subjectNamesFromStandards } from '../../utils/evaluationPlanDocTitle';
import type { EvaluationPlanDraft, EvaluationStandardDraft } from '../../types/evaluationPlan';
import {
  EMPTY_EVALUATION_LEVELS,
  EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS,
  LEVEL_STEP_COUNT_OPTIONS,
  buildPlanLevelsFromStandards,
  compareStandardsByCode,
  clampLevelStepCount,
  cloneEvaluationPlanAttachment,
  cloneEvaluationPlanDraft,
  copyEvaluationLevels,
  createEmptyPlanDraft,
  EVALUATION_LEVEL_KEYS,
  formatAchievementStandardLabel,
  formatLevelStepOptionLabel,
  getEvaluationPlanAttachment,
  hasEvaluationPlanSlotAttachment,
  parseEvaluationPlanAttachment,
  standardMatchesFilter,
  type EvaluationLevelDisplayLabelsByStepCount,
  type EvaluationLevelStepCount,
  getEvaluationPlanAttachmentRegisterMethod,
  getActiveLevelKeys,
  getActiveLevelLabels,
  getLevelDisplayLabelsForStep,
  formatLevelHeadLabel,
  type EvaluationPlanAttachment,
  type EvaluationPlanAttachmentSlot,
} from '../../types/evaluationPlan';
import EvaluationPlanAttachmentRegisterModal, {
  type EvaluationPlanAttachmentRegisterMode,
  type EvaluationPlanAttachmentRegisterStep,
} from './EvaluationPlanAttachmentRegisterModal';
import EvaluationPlanShareLinkActionPopover from './EvaluationPlanShareLinkActionPopover';
import EvaluationPlanWebDocumentEditorModal from './EvaluationPlanWebDocumentEditorModal';
import EvaluationPlanWebDocumentViewerModal from './EvaluationPlanWebDocumentViewerModal';
import EvaluationPlanSheetCanvasEditorModal from './EvaluationPlanSheetCanvasEditorModal';
import EvaluationPlanSheetCanvasViewerModal from './EvaluationPlanSheetCanvasViewerModal';
import { attachmentSupportsShareLinkActions, isSheetCanvasAttachment } from '../../utils/evaluationPlanAttachment';
import { sizeTextareaToContent } from '../todoNote/todoNoteRowShared';
import UnsavedChangesConfirmModal from '../common/UnsavedChangesConfirmModal';
import { clearEvaluationPlanSheetCanvasDraft } from '../../utils/evaluationPlanSheetCanvasDraftStorage';
import {
  revokePendingSheetImagesForPlan,
  uploadPendingSheetImagesForPlan,
} from '../../utils/evaluationSheetImageLifecycle';
import AiEvaluationComposeModal from './AiEvaluationComposeModal';
import { useAiAccess } from '../../hooks/useAiAccess';

const AutoGrowTextarea: React.FC<{
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
}> = ({ value, onChange, readOnly = false, autoFocus, 'aria-label': ariaLabel }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    sizeTextareaToContent(ref.current, 72);
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  return (
    <textarea
      ref={ref}
      className={`cp-todo-note-plain cp-todo-note-plain-memo cp-evaluation-plan-standard-form-textarea${readOnly ? ' is-readonly' : ''}`}
      rows={1}
      value={value}
      readOnly={readOnly}
      tabIndex={readOnly ? -1 : undefined}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      onInput={(e) => {
        if (readOnly || !onChange) return;
        onChange(e.currentTarget.value);
        sizeTextareaToContent(e.currentTarget, 72);
      }}
      onChange={(e) => {
        if (!readOnly) onChange?.(e.target.value);
      }}
    />
  );
};

const PlanFormDocAttachmentGroup: React.FC<{
  hasAttachment: boolean;
  slotAttachment: EvaluationPlanAttachment | null;
  registerLabel: string;
  registeredLabel: string;
  onRegister: () => void;
  onEdit: () => void;
  onOpenDocument?: () => void;
  onRequestDelete: () => void;
  tutorialEp?: string;
}> = ({
  hasAttachment,
  slotAttachment,
  registerLabel,
  registeredLabel,
  onRegister,
  onEdit,
  onOpenDocument,
  onRequestDelete,
  tutorialEp,
}) => (
  <div
    className={`cp-evaluation-plan-plan-form-doc-btn${hasAttachment ? ' is-registered' : ''}`}
    {...(tutorialEp ? { 'data-tutorial-ep': tutorialEp } : {})}
  >
    {/* 호버 팝업 없이: 등록됨 → 바로 문서 열기 / 미등록 → 등록 */}
    <button
      type="button"
      className="cp-evaluation-plan-plan-form-doc-btn-main"
      aria-pressed={hasAttachment}
      title={hasAttachment ? '문서 열기' : undefined}
      onClick={() => (hasAttachment ? onOpenDocument?.() : onRegister())}
    >
      <span className={`cp-evaluation-plan-round-check${hasAttachment ? ' is-checked' : ''}`} aria-hidden />
      <span className="cp-evaluation-plan-plan-form-doc-btn-label">
        {hasAttachment ? registeredLabel : registerLabel}
      </span>
    </button>
    {hasAttachment ? (
      <div
        className="cp-evaluation-plan-plan-form-doc-btn-tools"
        role="group"
        aria-label={`${registeredLabel} 도구`}
      >
        <button
          type="button"
          className="cp-evaluation-plan-plan-form-doc-icon-btn"
          style={{ '--cp-icon-url': `url(${process.env.PUBLIC_URL || ''}/edit.svg)` } as React.CSSProperties}
          title="수정"
          aria-label={`${registeredLabel} 수정`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <img src="/edit.svg" alt="" className="cp-admin-notices-tool-icon" width={15} height={15} decoding="async" />
        </button>
        <button
          type="button"
          className="cp-evaluation-plan-plan-form-doc-icon-btn"
          style={{ '--cp-icon-url': `url(${process.env.PUBLIC_URL || ''}/Trash.svg)` } as React.CSSProperties}
          title="삭제"
          aria-label={`${registeredLabel} 삭제`}
          onClick={(e) => {
            e.stopPropagation();
            onRequestDelete();
          }}
        >
          <img src="/Trash.svg" alt="" className="cp-admin-notices-tool-icon" width={15} height={15} decoding="async" />
        </button>
      </div>
    ) : null}
  </div>
);

const EvaluationPlanFormModal: React.FC<{
  open: boolean;
  mode: 'create' | 'edit';
  categoryName: string;
  standards: EvaluationStandardDraft[];
  initial: EvaluationPlanDraft | null;
  initialStandardIds?: string[];
  sortOrder: number;
  /** 신규 작성 시 기본 단계(기본 설정). 수정 시에는 plan.level_step_count 사용 */
  defaultLevelStepCount: EvaluationLevelStepCount;
  levelLabelsByStep?: EvaluationLevelDisplayLabelsByStepCount | null;
  onClose: () => void;
  onSubmit: (plan: EvaluationPlanDraft) => void | Promise<unknown>;
  onDraftChange?: (plan: EvaluationPlanDraft) => void;
  readOnly?: boolean;
  workspaceId?: string | null;
  imageScope?: 'admin' | 'personal';
  /** 사용자 화면: AI 평가 구성 학습내용 검색 범위(현재 학급 학년) */
  aiClassScope?: { gradeId: string; gradeNumbers: number[] } | null;
}> = ({
  open,
  mode,
  categoryName,
  standards,
  initial,
  initialStandardIds = [],
  sortOrder,
  defaultLevelStepCount,
  levelLabelsByStep,
  onClose,
  onSubmit,
  onDraftChange,
  readOnly = false,
  workspaceId = null,
  imageScope = 'admin',
  aiClassScope = null,
}) => {
  const [draft, setDraft] = useState<EvaluationPlanDraft | null>(null);
  const [deleteAttachmentSlot, setDeleteAttachmentSlot] = useState<EvaluationPlanAttachmentSlot | null>(null);
  const [attachmentRegister, setAttachmentRegister] = useState<{
    slot: EvaluationPlanAttachmentSlot;
    step: EvaluationPlanAttachmentRegisterStep;
    mode: EvaluationPlanAttachmentRegisterMode;
    initialShareUrl: string;
  } | null>(null);
  const [webDocumentEditorSlot, setWebDocumentEditorSlot] = useState<EvaluationPlanAttachmentSlot | null>(null);
  const [sheetCanvasEditorOpen, setSheetCanvasEditorOpen] = useState(false);
  const [webDocumentViewerSlot, setWebDocumentViewerSlot] = useState<EvaluationPlanAttachmentSlot | null>(null);
  const [sheetCanvasViewerOpen, setSheetCanvasViewerOpen] = useState(false);
  const [standardPickerFilterQuery, setStandardPickerFilterQuery] = useState('');
  const [standardPickerListFilter, setStandardPickerListFilter] = useState<'all' | 'selected'>('all');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** AI 평가 구성: 이용권별 권한(AI 평가계획·평가기준안·평가지 생성 기능)으로 노출. 관리자는 항상 허용(서버도 동일하게 검사) */
  const isAiMaster = useAiAccess().canPlan('ai_evaluation');
  const [aiComposeOpen, setAiComposeOpen] = useState(false);
  /** AI 적용 전 초안 스냅샷(한 번에 되돌리기). 모달을 닫으면 초기화 */
  const aiSnapshotRef = useRef<EvaluationPlanDraft | null>(null);
  const [aiHasSnapshot, setAiHasSnapshot] = useState(false);
  /** 열었을 때의 초안(JSON) — 변경사항 판별용 */
  const draftBaselineRef = useRef<string | null>(null);

  const sortedStandards = useMemo(() => [...standards].sort(compareStandardsByCode), [standards]);
  const standardPickerListFilterEffective = readOnly ? 'selected' : standardPickerListFilter;
  const visibleStandards = useMemo(() => {
    const selectedIds = new Set(draft?.standard_ids ?? []);
    return sortedStandards.filter((row) => {
      if (standardPickerListFilterEffective === 'selected' && !selectedIds.has(row.id)) return false;
      return standardMatchesFilter(row, standardPickerFilterQuery);
    });
  }, [sortedStandards, standardPickerFilterQuery, standardPickerListFilterEffective, draft?.standard_ids]);
  const draftSeedKeyRef = useRef<string | null>(null);
  const createStandardIdsKey = initialStandardIds.join(',');

  useEffect(() => {
    if (!open || !categoryName.trim()) {
      draftSeedKeyRef.current = null;
      draftBaselineRef.current = null;
      setCloseConfirmOpen(false);
      setSubmitting(false);
      setDraft(null);
      setStandardPickerFilterQuery('');
      setStandardPickerListFilter(readOnly ? 'selected' : 'all');
      setDeleteAttachmentSlot(null);
      setAttachmentRegister(null);
      setWebDocumentEditorSlot(null);
      setSheetCanvasEditorOpen(false);
      setWebDocumentViewerSlot(null);
      setSheetCanvasViewerOpen(false);
      setAiComposeOpen(false);
      aiSnapshotRef.current = null;
      setAiHasSnapshot(false);
      return;
    }

    const seedKey = initial
      ? `edit:${initial.id}`
      : `create:${categoryName}:${sortOrder}:${createStandardIdsKey}`;

    if (draftSeedKeyRef.current === seedKey) {
      return;
    }

    draftSeedKeyRef.current = seedKey;
    if (initial) {
      const cloned = cloneEvaluationPlanDraft(initial);
      const step = clampLevelStepCount(cloned.level_step_count ?? defaultLevelStepCount);
      const selected = standards.filter((row) => (cloned.standard_ids ?? []).includes(row.id));
      const hasStoredLevel = EVALUATION_LEVEL_KEYS.some((key) => (cloned[key] ?? '').trim());
      const seeded: EvaluationPlanDraft = {
        ...cloned,
        level_step_count: step,
        ...(!hasStoredLevel && selected.length > 0 ? buildPlanLevelsFromStandards(selected, step) : {}),
      };
      draftBaselineRef.current = JSON.stringify(seeded);
      setDraft(seeded);
      return;
    }
    const created = createEmptyPlanDraft(categoryName, sortOrder, initialStandardIds, defaultLevelStepCount);
    const selected = standards.filter((row) => (created.standard_ids ?? []).includes(row.id));
    const seededCreate: EvaluationPlanDraft = {
      ...created,
      ...buildPlanLevelsFromStandards(selected, clampLevelStepCount(created.level_step_count ?? defaultLevelStepCount)),
    };
    draftBaselineRef.current = JSON.stringify(seededCreate);
    setDraft(seededCreate);
  }, [open, categoryName, initial, sortOrder, createStandardIdsKey, readOnly, defaultLevelStepCount, standards]);

  if (!open || !categoryName.trim() || !draft) return null;

  const sheetCanvasDraftKey = draft.id ?? `create:${categoryName}:${sortOrder}`;
  const isFormDirty =
    !readOnly && draftBaselineRef.current != null && JSON.stringify(draft) !== draftBaselineRef.current;

  /** 저장하지 않고 닫기: 평가지 캔버스 로컬 초안·로컬 이미지 정리 (서버에는 아무것도 하지 않음) */
  const discardAndClose = () => {
    clearEvaluationPlanSheetCanvasDraft(sheetCanvasDraftKey);
    revokePendingSheetImagesForPlan(draft);
    onClose();
  };

  /** 닫기·바깥 클릭·Esc */
  const requestClose = () => {
    if (submitting) return;
    if (isFormDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    discardAndClose();
  };

  /** 저장: 평가지 로컬 이미지를 먼저 업로드한 뒤 평가계획 행 저장(onSubmit) */
  const handleSubmit = async () => {
    if (readOnly || submitting) return;
    setSubmitting(true);
    try {
      const base: EvaluationPlanDraft = {
        ...draft,
        category_name: categoryName,
        standard_ids: [...(draft.standard_ids ?? [])],
        sort_order: draft.sort_order ?? sortOrder,
        criterion_rubric_attachment: cloneEvaluationPlanAttachment(draft.criterion_rubric_attachment),
        evaluation_sheet_attachment: cloneEvaluationPlanAttachment(draft.evaluation_sheet_attachment),
        ...copyEvaluationLevels(draft),
      };
      let uploaded: EvaluationPlanDraft;
      try {
        uploaded = (await uploadPendingSheetImagesForPlan(base, imageScope, workspaceId)).plan;
      } catch (error) {
        console.error('평가지 이미지 업로드 실패:', error);
        alert(
          error instanceof Error && error.message === 'QUOTA_EXCEEDED'
            ? '이미지 저장 용량을 초과했습니다.'
            : '평가지 이미지 업로드에 실패했습니다. 네트워크를 확인해 주세요.'
        );
        return;
      }
      await onSubmit(uploaded);
      clearEvaluationPlanSheetCanvasDraft(sheetCanvasDraftKey);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const levelStepCount = clampLevelStepCount(draft.level_step_count ?? defaultLevelStepCount);
  const levelDisplayLabels = getLevelDisplayLabelsForStep(
    levelLabelsByStep ?? {
      2: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
      3: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
      4: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
      5: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
    },
    levelStepCount
  );

  const syncDraftToWorkspace = (next: EvaluationPlanDraft) => {
    if (mode === 'edit') onDraftChange?.(cloneEvaluationPlanDraft(next));
  };

  const patch = (patchValue: Partial<EvaluationPlanDraft>) => {
    if (readOnly) return;
    setDraft((prev) => (prev ? { ...prev, ...patchValue } : prev));
  };

  const openAttachmentRegister = (
    slot: EvaluationPlanAttachmentSlot,
    step: EvaluationPlanAttachmentRegisterStep,
    registerMode: EvaluationPlanAttachmentRegisterMode = 'register',
    initialShareUrl = ''
  ) => {
    setAttachmentRegister({ slot, step, mode: registerMode, initialShareUrl });
  };

  const saveAttachment = (slot: EvaluationPlanAttachmentSlot, attachment: EvaluationPlanAttachment) => {
    const nextAttachment: EvaluationPlanAttachment = {
      ...attachment,
      slot,
      form_data: attachment.form_data ? { ...attachment.form_data } : undefined,
    };
    let nextPlan: EvaluationPlanDraft | null = null;
    setDraft((prev) => {
      if (!prev) return prev;
      nextPlan =
        slot === 'criterion'
          ? { ...prev, criterion_rubric_attachment: nextAttachment }
          : { ...prev, evaluation_sheet_attachment: nextAttachment };
      return nextPlan;
    });
    if (nextPlan) syncDraftToWorkspace(nextPlan);
  };

  const getSlotAttachment = (slot: EvaluationPlanAttachmentSlot): EvaluationPlanAttachment | null => {
    const raw =
      slot === 'criterion' ? draft.criterion_rubric_attachment : draft.evaluation_sheet_attachment;
    return parseEvaluationPlanAttachment(raw) ?? (raw && typeof raw === 'object' ? (raw as EvaluationPlanAttachment) : null);
  };

  const handleAttachmentEdit = (slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getSlotAttachment(slot);
    const shareUrl = attachment?.share_url?.trim();
    if (attachmentSupportsShareLinkActions(attachment) && shareUrl) {
      openAttachmentRegister(slot, 'share-link', 'edit', shareUrl);
      return;
    }
    if (getEvaluationPlanAttachmentRegisterMethod(attachment) === 'web_document') {
      if (slot === 'sheet') {
        setSheetCanvasEditorOpen(true);
      } else {
        setWebDocumentEditorSlot(slot);
      }
      return;
    }
    if (hasEvaluationPlanSlotAttachment(draft, slot)) {
      openAttachmentRegister(slot, 'choose', 'register');
    }
  };

  const clearAttachment = (slot: EvaluationPlanAttachmentSlot) => {
    let nextPlan: EvaluationPlanDraft | null = null;
    setDraft((prev) => {
      if (!prev) return prev;
      nextPlan =
        slot === 'criterion'
          ? { ...prev, criterion_rubric_attachment: null }
          : { ...prev, evaluation_sheet_attachment: null };
      return nextPlan;
    });
    if (nextPlan) syncDraftToWorkspace(nextPlan);
  };

  const toggleStandardId = (standardId: string) => {
    if (readOnly) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const current = prev.standard_ids ?? [];
      const nextIds = current.includes(standardId)
        ? current.filter((id) => id !== standardId)
        : [...current, standardId];
      const step = clampLevelStepCount(prev.level_step_count ?? defaultLevelStepCount);
      const selected = sortedStandards.filter((row) => nextIds.includes(row.id));
      return {
        ...prev,
        standard_ids: nextIds,
        ...buildPlanLevelsFromStandards(selected, step),
      };
    });
  };

  /** AI 첫 적용 직전 초안 저장 (되돌리기용) */
  const ensureAiSnapshot = () => {
    if (aiSnapshotRef.current) return;
    aiSnapshotRef.current = cloneEvaluationPlanDraft(draft);
    setAiHasSnapshot(true);
  };

  const hasCriterion = Boolean(getSlotAttachment('criterion'));
  const hasSheet = Boolean(getSlotAttachment('sheet'));

  const selectedStandards = sortedStandards.filter((row) => (draft.standard_ids ?? []).includes(row.id));
  /** 기준안·평가지 제목에 넣을 과목명(성취기준 정렬 순서, 중복 제거) */
  const planSubjectNames = subjectNamesFromStandards(selectedStandards);
  const reflectedAreaText =
    selectedStandards
      .map((row) => (row.area_text ?? '').trim())
      .filter(Boolean)
      .join('\n') || '—';
  const reflectedAchievementText =
    selectedStandards
      .map((row) => formatAchievementStandardLabel(row).trim())
      .filter(Boolean)
      .join('\n') || '—';

  const selectedStandardCount = (draft.standard_ids ?? []).length;
  const standardPickerEmptyMessage =
    standardPickerListFilterEffective === 'selected' && selectedStandardCount === 0
      ? '선택된 성취기준이 없습니다.'
      : '검색 결과가 없습니다.';

  const deleteAttachmentLabel = deleteAttachmentSlot
    ? EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[deleteAttachmentSlot]
    : '';

  const openDocumentViewer = (slot: EvaluationPlanAttachmentSlot) => {
    const attachment = getSlotAttachment(slot);
    if (slot === 'sheet' && isSheetCanvasAttachment(attachment)) {
      setSheetCanvasViewerOpen(true);
      return;
    }
    setWebDocumentViewerSlot(slot);
  };

  return (
    <>
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') requestClose();
      }}
    >
      <div
        className="cp-modal cp-evaluation-plan-standard-form-modal cp-evaluation-plan-plan-form-modal"
        role="dialog"
        aria-modal="true"
        data-tutorial-ep="plan-form-modal"
        aria-labelledby="cp-evaluation-plan-plan-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-evaluation-plan-plan-form-header">
          <h3 id="cp-evaluation-plan-plan-form-title">
            {readOnly ? '평가계획 보기' : mode === 'create' ? '평가계획 등록' : '평가계획 수정'}
          </h3>
          {!readOnly && isAiMaster ? (
            <button
              type="button"
              className="cp-ai-feedback-btn cp-evaluation-plan-ai-compose-btn"
              onClick={() => setAiComposeOpen(true)}
              title="성취기준·학습내용으로 평가계획·기준안·평가지를 AI로 구성"
            >
              <AiSparkIcon />
              AI 평가 구성
            </button>
          ) : null}
          <div className="cp-modal-document-actions cp-evaluation-plan-plan-form-header-actions">
            <label className="cp-evaluation-plan-plan-form-level-step" data-tutorial-ep="plan-level-step">
              <span className="cp-evaluation-plan-plan-form-level-step-label">성취수준 단계</span>
              <select
                value={levelStepCount}
                disabled={readOnly}
                aria-label="성취수준 단계"
                onChange={(e) => {
                  const nextStep = clampLevelStepCount(Number(e.target.value));
                  const selected = sortedStandards.filter((row) => (draft.standard_ids ?? []).includes(row.id));
                  const next = {
                    ...draft,
                    level_step_count: nextStep,
                    ...(selected.length > 0 ? buildPlanLevelsFromStandards(selected, nextStep) : {}),
                  };
                  setDraft(next);
                  syncDraftToWorkspace(next);
                }}
              >
                {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {formatLevelStepOptionLabel(
                      count,
                      getLevelDisplayLabelsForStep(
                        levelLabelsByStep ?? {
                          2: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
                          3: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
                          4: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
                          5: { level_label_a: '', level_label_b: '', level_label_c: '', level_label_d: '', level_label_e: '' },
                        },
                        count
                      )
                    )}
                  </option>
                ))}
              </select>
            </label>
            {readOnly ? (
              <>
                <button
                  type="button"
                  className={`cp-evaluation-plan-plan-form-doc-btn cp-evaluation-plan-plan-form-view-doc-btn${
                    hasCriterion ? ' is-registered' : ''
                  }`}
                  disabled={!hasCriterion}
                  title={hasCriterion ? '기준안 보기' : '등록된 기준안이 없습니다'}
                  aria-label={hasCriterion ? '기준안 보기' : '등록된 기준안 없음'}
                  onClick={() => {
                    if (!hasCriterion) return;
                    openDocumentViewer('criterion');
                  }}
                >
                  <span
                    className={`cp-evaluation-plan-round-check${hasCriterion ? ' is-checked' : ''}`}
                    aria-hidden
                  />
                  <span className="cp-evaluation-plan-plan-form-doc-btn-label">기준안 보기</span>
                </button>
                <button
                  type="button"
                  className={`cp-evaluation-plan-plan-form-doc-btn cp-evaluation-plan-plan-form-view-doc-btn${
                    hasSheet ? ' is-registered' : ''
                  }`}
                  disabled={!hasSheet}
                  title={hasSheet ? '평가지 보기' : '등록된 평가지가 없습니다'}
                  aria-label={hasSheet ? '평가지 보기' : '등록된 평가지 없음'}
                  onClick={() => {
                    if (!hasSheet) return;
                    openDocumentViewer('sheet');
                  }}
                >
                  <span
                    className={`cp-evaluation-plan-round-check${hasSheet ? ' is-checked' : ''}`}
                    aria-hidden
                  />
                  <span className="cp-evaluation-plan-plan-form-doc-btn-label">평가지 보기</span>
                </button>
              </>
            ) : (
              <>
                <PlanFormDocAttachmentGroup
                  hasAttachment={hasCriterion}
                  slotAttachment={getSlotAttachment('criterion')}
                  registerLabel="기준안 등록"
                  registeredLabel="기준안 등록됨"
                  tutorialEp="plan-register-criterion"
                  onRegister={() => openAttachmentRegister('criterion', 'choose', 'register')}
                  onEdit={() => handleAttachmentEdit('criterion')}
                  onOpenDocument={() => openDocumentViewer('criterion')}
                  onRequestDelete={() => setDeleteAttachmentSlot('criterion')}
                />
                <PlanFormDocAttachmentGroup
                  hasAttachment={hasSheet}
                  slotAttachment={getSlotAttachment('sheet')}
                  registerLabel="평가지 등록"
                  registeredLabel="평가지 등록됨"
                  tutorialEp="plan-register-sheet"
                  onRegister={() => openAttachmentRegister('sheet', 'choose', 'register')}
                  onEdit={() => handleAttachmentEdit('sheet')}
                  onOpenDocument={() => openDocumentViewer('sheet')}
                  onRequestDelete={() => setDeleteAttachmentSlot('sheet')}
                />
              </>
            )}
          </div>
        </div>
        <div className="cp-modal-body cp-evaluation-plan-form-body">
          <section className="cp-evaluation-plan-plan-standard-picker" aria-label="연결 성취기준">
            {sortedStandards.length === 0 ? (
              <p className="cp-evaluation-plan-plan-standard-picker-empty" role="status">
                카테고리에 등록된 성취기준이 없습니다. 왼쪽에서 성취기준을 먼저 등록해 주세요.
              </p>
            ) : (
              <>
                <div className="cp-evaluation-plan-plan-standard-picker-toolbar">
                  <p className="cp-evaluation-plan-plan-standard-picker-selected-count" role="status">
                    선택 {selectedStandardCount}개
                  </p>
                  <label className="cp-evaluation-plan-plan-standard-picker-search">
                    <input
                      type="search"
                      className="cp-evaluation-plan-filter-input cp-evaluation-plan-plan-standard-picker-search-input"
                      placeholder="영역·성취기준 검색"
                      value={standardPickerFilterQuery}
                      onChange={(e) => setStandardPickerFilterQuery(e.target.value)}
                      aria-label="성취기준 검색"
                      disabled={readOnly}
                    />
                  </label>
                  {!readOnly ? (
                  <div
                    className="cp-evaluation-plan-plan-standard-picker-filter-actions"
                    role="group"
                    aria-label="성취기준 목록 필터"
                  >
                    <button
                      type="button"
                      className={`cp-evaluation-plan-plan-standard-picker-filter-btn${standardPickerListFilter === 'selected' ? ' is-active' : ''}`}
                      aria-pressed={standardPickerListFilter === 'selected'}
                      onClick={() => setStandardPickerListFilter('selected')}
                    >
                      선택 성취기준 보기
                    </button>
                    <button
                      type="button"
                      className={`cp-evaluation-plan-plan-standard-picker-filter-btn${standardPickerListFilter === 'all' ? ' is-active' : ''}`}
                      aria-pressed={standardPickerListFilter === 'all'}
                      onClick={() => setStandardPickerListFilter('all')}
                    >
                      전체보기
                    </button>
                  </div>
                  ) : null}
                </div>
                {visibleStandards.length === 0 ? (
                  <p className="cp-evaluation-plan-plan-standard-picker-empty" role="status">
                    {standardPickerEmptyMessage}
                  </p>
                ) : (
              <div className="cp-evaluation-plan-plan-standard-picker-table-wrap">
                <table className="cp-evaluation-plan-plan-standard-picker-table">
                  <thead>
                    <tr>
                      <th scope="col" className="is-col-check" aria-label="선택" />
                      <th scope="col" className="is-col-area">영역</th>
                      <th scope="col" className="is-col-achievement">성취기준</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStandards.map((standard) => {
                      const checked = (draft.standard_ids ?? []).includes(standard.id);
                      const area = (standard.area_text ?? '').trim() || '—';
                      const achievement =
                        formatAchievementStandardLabel(standard).trim() ||
                        (standard.achievement_code ?? '').trim() ||
                        '—';
                      return (
                        <tr
                          key={standard.id}
                          className={`cp-evaluation-plan-plan-standard-picker-row${checked ? ' is-checked' : ''}${readOnly ? ' is-readonly' : ''}`}
                          role={readOnly ? undefined : 'checkbox'}
                          aria-checked={readOnly ? undefined : checked}
                          aria-label={`${area} / ${achievement}`}
                          tabIndex={readOnly ? undefined : 0}
                          onClick={readOnly ? undefined : () => toggleStandardId(standard.id)}
                          onKeyDown={
                            readOnly
                              ? undefined
                              : (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleStandardId(standard.id);
                                  }
                                }
                          }
                        >
                          <td className="is-col-check">
                            <span
                              className={`cp-evaluation-plan-round-check${checked ? ' is-checked' : ''}`}
                              aria-hidden
                            />
                          </td>
                          <td className="is-col-area">{area}</td>
                          <td className="is-col-achievement">{achievement}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
                )}
              </>
            )}
          </section>

          <div className="cp-todo-note-editor-blocks cp-evaluation-plan-standard-form-row cp-evaluation-plan-standard-form-row--single cp-evaluation-plan-plan-form-row">
            <div className="cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--area">
              <div className="cp-todo-note-editor-block-head">영역</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={reflectedAreaText}
                  readOnly
                  aria-label="영역"
                />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--achievement">
              <div className="cp-todo-note-editor-block-head">성취기준</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={reflectedAchievementText}
                  readOnly
                  aria-label="성취기준"
                />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--unit">
              <div className="cp-todo-note-editor-block-head">단원명</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={draft.unit_text}
                  onChange={(v) => patch({ unit_text: v })}
                  readOnly={readOnly}
                  autoFocus={!readOnly}
                  aria-label="단원명"
                />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--element">
              <div className="cp-todo-note-editor-block-head">평가요소</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={draft.evaluation_element_text}
                  readOnly={readOnly}
                  onChange={(v) => patch({ evaluation_element_text: v })}
                  aria-label="평가요소"
                />
              </div>
            </div>
            <div className="cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--method">
              <div className="cp-todo-note-editor-block-head">수업·평가 방법</div>
              <div className="cp-todo-note-editor-block-body">
                <AutoGrowTextarea
                  value={draft.evaluation_method_text ?? ''}
                  readOnly={readOnly}
                  onChange={(v) => patch({ evaluation_method_text: v })}
                  aria-label="수업·평가 방법"
                />
              </div>
            </div>
            {/* '수업·평가방법, 연계의 주안점'(lesson_evaluation_text) 카드는 모달에서 숨김 — DB 값은 보존(저장 시 기존 값 그대로 전달) */}
            {getActiveLevelKeys(levelStepCount).map((key, idx) => {
              const levelLabel = getActiveLevelLabels(levelStepCount, levelDisplayLabels)[idx];
              const headLabel = formatLevelHeadLabel(levelLabel);
              const levelLetter = key.replace('level_', '');
              return (
                <div
                  key={key}
                  className={`cp-todo-note-editor-block cp-evaluation-plan-plan-form-block--level cp-evaluation-plan-plan-form-block--level-${levelLetter}`}
                >
                  <div className="cp-todo-note-editor-block-head">{headLabel}</div>
                  <div className="cp-todo-note-editor-block-body">
                    <AutoGrowTextarea
                      value={draft[key]}
                      readOnly={readOnly}
                      onChange={(v) => patch({ [key]: v })}
                      aria-label={headLabel}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={requestClose} disabled={submitting}>
            닫기
          </button>
          {!readOnly ? (
            <button
              type="button"
              className="cp-btn-primary"
              disabled={submitting}
              onClick={() => void handleSubmit()}
            >
              {submitting ? '저장 중…' : '저장'}
            </button>
          ) : null}
        </div>
      </div>
    </div>

    <UnsavedChangesConfirmModal
      open={closeConfirmOpen}
      primaryDisabled={submitting}
      onCancel={() => setCloseConfirmOpen(false)}
      onDiscard={() => {
        setCloseConfirmOpen(false);
        discardAndClose();
      }}
      onPrimary={() => {
        setCloseConfirmOpen(false);
        void handleSubmit();
      }}
    />

    {deleteAttachmentSlot ? (
      <div
        className="cp-modal-backdrop cp-evaluation-plan-plan-form-delete-backdrop"
        role="presentation"
        onClick={() => setDeleteAttachmentSlot(null)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setDeleteAttachmentSlot(null);
        }}
      >
        <div
          className="cp-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-evaluation-plan-plan-form-delete-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="cp-evaluation-plan-plan-form-delete-title">{deleteAttachmentLabel} 삭제</h3>
          <div className="cp-modal-body">
            <p>
              등록된 {deleteAttachmentLabel} 데이터가 삭제됩니다. 계속하시겠습니까?
            </p>
          </div>
          <div className="cp-modal-footer">
            <button type="button" className="cp-btn-secondary" onClick={() => setDeleteAttachmentSlot(null)}>
              취소
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => {
                clearAttachment(deleteAttachmentSlot);
                setDeleteAttachmentSlot(null);
              }}
            >
              삭제
            </button>
          </div>
        </div>
      </div>
    ) : null}

    <EvaluationPlanAttachmentRegisterModal
      open={attachmentRegister != null}
      slot={attachmentRegister?.slot ?? null}
      step={attachmentRegister?.step ?? 'choose'}
      mode={attachmentRegister?.mode ?? 'register'}
      existingAttachment={
        attachmentRegister ? getEvaluationPlanAttachment(draft, attachmentRegister.slot) : null
      }
      initialShareUrl={attachmentRegister?.initialShareUrl ?? ''}
      onClose={() => setAttachmentRegister(null)}
      onStepChange={(step) =>
        setAttachmentRegister((prev) => (prev ? { ...prev, step } : prev))
      }
      onOpenWebDocument={(slot) => {
        setAttachmentRegister(null);
        if (slot === 'sheet') {
          setSheetCanvasEditorOpen(true);
        } else {
          setWebDocumentEditorSlot(slot);
        }
      }}
      onSaveShareLink={(slot, attachment) => {
        saveAttachment(slot, attachment);
        setAttachmentRegister(null);
      }}
    />

    <EvaluationPlanWebDocumentViewerModal
      subjectNames={planSubjectNames}
      open={webDocumentViewerSlot != null}
      slot={webDocumentViewerSlot}
      attachment={
        webDocumentViewerSlot ? getEvaluationPlanAttachment(draft, webDocumentViewerSlot) : null
      }
      levelStepCount={levelStepCount}
      levelDisplayLabels={levelDisplayLabels}
      onClose={() => setWebDocumentViewerSlot(null)}
    />

    <EvaluationPlanSheetCanvasViewerModal
      subjectNames={planSubjectNames}
      open={sheetCanvasViewerOpen}
      slot="sheet"
      attachment={getEvaluationPlanAttachment(draft, 'sheet')}
      onClose={() => setSheetCanvasViewerOpen(false)}
    />

    <EvaluationPlanWebDocumentEditorModal
      open={webDocumentEditorSlot != null}
      slot={webDocumentEditorSlot}
      existingAttachment={
        webDocumentEditorSlot ? getEvaluationPlanAttachment(draft, webDocumentEditorSlot) : null
      }
      planSource={draft}
      standards={sortedStandards}
      levelStepCount={levelStepCount}
      levelDisplayLabels={levelDisplayLabels}
      onClose={() => setWebDocumentEditorSlot(null)}
      onSave={(slot, attachment) => {
        saveAttachment(slot, attachment);
      }}
    />

    {!readOnly && isAiMaster ? (
      <AiEvaluationComposeModal
        open={aiComposeOpen}
        onClose={() => setAiComposeOpen(false)}
        workspaceId={workspaceId}
        categoryName={categoryName}
        draft={draft}
        allStandards={sortedStandards}
        levelStepCount={levelStepCount}
        levelDisplayLabels={levelDisplayLabels}
        hasCriterion={hasCriterion}
        hasSheet={hasSheet}
        existingCriterionAttachment={getSlotAttachment('criterion')}
        existingSheetAttachment={getSlotAttachment('sheet')}
        canUndo={aiHasSnapshot}
        classScope={aiClassScope}
        onApplyFields={(patchValue) => {
          ensureAiSnapshot();
          const next = { ...draft, ...patchValue };
          setDraft(next);
          syncDraftToWorkspace(next);
        }}
        onApplyAttachment={(slot, attachment) => {
          ensureAiSnapshot();
          if (slot === 'sheet') clearEvaluationPlanSheetCanvasDraft(sheetCanvasDraftKey);
          saveAttachment(slot, attachment);
        }}
        onUndo={() => {
          const snap = aiSnapshotRef.current;
          if (!snap) return;
          clearEvaluationPlanSheetCanvasDraft(sheetCanvasDraftKey);
          const restored = cloneEvaluationPlanDraft(snap);
          setDraft(restored);
          syncDraftToWorkspace(restored);
          aiSnapshotRef.current = null;
          setAiHasSnapshot(false);
        }}
      />
    ) : null}

    <EvaluationPlanSheetCanvasEditorModal
      subjectNames={planSubjectNames}
      open={sheetCanvasEditorOpen}
      slot="sheet"
      existingAttachment={getEvaluationPlanAttachment(draft, 'sheet')}
      draftStorageKey={sheetCanvasDraftKey}
      imageScope={imageScope}
      workspaceId={workspaceId}
      onClose={() => setSheetCanvasEditorOpen(false)}
      onSave={(slot, attachment) => {
        saveAttachment(slot, attachment);
      }}
    />
    </>
  );
};

export default EvaluationPlanFormModal;
